import { getMCPMonitorGrowthRate } from "@/api/endpoints/searches";
import { useI18n } from "@/contexts/i18nContext";
import { cn } from "@/lib/utils";
import { SearchFilterV4WithDates } from "@/types/SearchFilter";
import { formatDateForApi } from "@/utils";
import {
  Widget,
  WidgetContent,
  WidgetHeader,
  WidgetProps,
  WidgetStat,
  WidgetTitle,
  WidgetTrend,
} from "@components/ui/widget";
import { getMCPMonitorFiltersFromSearchFilterV4 } from "@components/widgets/utils";
import { Trans } from "@lingui/react/macro";
import { useQuery } from "@tanstack/react-query";
import { subDays } from "date-fns";

export default function MentionCountWidget({
  searchUuid,
  filters,
  diffInDays,
  className,
  ...rest
}: WidgetProps & {
  searchUuid: Uuid;
  filters: SearchFilterV4WithDates;
  diffInDays: number;
}) {
  const { abbreviateNumber } = useI18n();

  // Server state
  const { data, isLoading } = useQuery({
    queryKey: [searchUuid, "insights-results", "total-mentions", filters],
    queryFn: async () => {
      return getMCPMonitorGrowthRate(searchUuid, {
        ...getMCPMonitorFiltersFromSearchFilterV4(filters),
        compare_start_date: formatDateForApi(
          subDays(new Date(filters.start_date), diffInDays + 1)
        ),
        compare_end_date: formatDateForApi(
          subDays(new Date(filters.end_date), diffInDays + 1)
        ),
        metric: "mention_count",
      });
    },
  });

  return (
    <Widget className={cn("gap-0.5", className)} {...rest}>
      <WidgetHeader isLoading={isLoading}>
        <WidgetTitle>
          <Trans>Totalt antal träffar</Trans>
        </WidgetTitle>
        {data?.ok ? (
          <WidgetStat>{abbreviateNumber(data.data.value)}</WidgetStat>
        ) : null}
      </WidgetHeader>
      <WidgetContent>
        {data?.ok ? <WidgetTrend value={data.data.change_pct / 100} /> : null}
      </WidgetContent>
    </Widget>
  );
}
